import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/use-async';
import { AdapterInfo, ApiModule } from '../lib/types';
import { formatDateTime } from '../lib/format';
import { Badge, Card, Empty, Field, Modal, Notice, PageHead } from '../components/ui';

export function Modules() {
  const modules = useAsync(() => api.get<ApiModule[]>('/api/admin/modules'));
  const adapters = useAsync(() => api.get<AdapterInfo[]>('/api/admin/modules/adapters'));
  const [creating, setCreating] = useState(false);

  return (
    <>
      <PageHead
        title="API modules"
        description="Every module maps one target API: its own path, its own specification, its own credentials. What is not in the active specification does not exist for the gateway."
        actions={
          <button className="primary" onClick={() => setCreating(true)}>
            Create module
          </button>
        }
      />

      {modules.error ? <Notice tone="danger">{modules.error}</Notice> : null}

      <Card tight>
        {modules.data?.length ? (
          <table>
            <thead>
              <tr>
                <th>Key</th>
                <th>Name</th>
                <th>Adapter</th>
                <th>Active specification</th>
                <th className="num">Operations</th>
                <th>Credentials</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {modules.data.map((module) => (
                <tr key={module.id}>
                  <td className="mono">
                    <Link to={`/modules/${module.id}`}>{module.key}</Link>
                  </td>
                  <td>{module.name}</td>
                  <td className="mono muted">{module.adapter}</td>
                  <td>
                    {module.activeSpec ? (
                      <>
                        {module.activeSpec.label}
                        <div className="muted tiny">
                          {formatDateTime(module.activeSpec.imported_at)}
                        </div>
                      </>
                    ) : (
                      <Badge tone="warn">none active</Badge>
                    )}
                  </td>
                  <td className="num">{module.activeSpec?.operation_count ?? 0}</td>
                  <td>
                    <Badge tone={module.usableCredentialCount ? 'ok' : 'danger'}>
                      {module.usableCredentialCount} of {module.credentialCount} usable
                    </Badge>
                  </td>
                  <td>
                    <Badge tone={module.active ? 'ok' : 'warn'}>{module.active ? 'active' : 'inactive'}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No module created yet.</Empty>
        )}
      </Card>

      {creating ? (
        <CreateModuleDialog
          adapters={adapters.data ?? []}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            modules.reload();
          }}
        />
      ) : null}
    </>
  );
}

function CreateModuleDialog({
  adapters,
  onClose,
  onCreated,
}: {
  adapters: AdapterInfo[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [adapter, setAdapter] = useState(adapters[0]?.key ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/admin/modules', { key, name, description: description || undefined, adapter });
      onCreated();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Create module"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={() => void submit()} disabled={busy || !key || !name || !adapter}>
            Create
          </button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Field label="Key" hint="becomes part of the path, e.g. /gw/autotask">
        <input
          type="text"
          value={key}
          onChange={(event) => setKey(event.target.value.toLowerCase())}
          placeholder="autotask"
        />
      </Field>
      <Field label="Name">
        <input type="text" value={name} onChange={(event) => setName(event.target.value)} placeholder="Autotask PSA" />
      </Field>
      <Field label="Description" hint="optional">
        <input type="text" value={description} onChange={(event) => setDescription(event.target.value)} />
      </Field>
      <Field label="Adapter" hint="encapsulates the specifics of the target API">
        <select value={adapter} onChange={(event) => setAdapter(event.target.value)}>
          {adapters.map((entry) => (
            <option key={entry.key} value={entry.key}>
              {entry.displayName}
            </option>
          ))}
        </select>
      </Field>
      <p className="muted tiny">
        After creation the specification and at least one credential follow. Only then does the module accept
        requests.
      </p>
    </Modal>
  );
}
